'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

// Read configured prices from env vars (set at build/deploy time).
// During the $0.50 test period these will be "0.50"; in production "14.99" / "9.99".
const BASE_PRICE_AMOUNT = parseFloat(process.env.NEXT_PUBLIC_STRIPE_BASE_PRICE_AMOUNT ?? '0');
const ADDITIONAL_PRICE_AMOUNT = parseFloat(process.env.NEXT_PUBLIC_STRIPE_ADDITIONAL_CITY_PRICE_AMOUNT ?? '0');

function formatPrice(amount: number): { dollars: string; cents: string } {
  const [dollars, cents] = amount.toFixed(2).split('.');
  return { dollars, cents };
}

export default function SubscribePage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const basePrice = formatPrice(BASE_PRICE_AMOUNT);
  const additionalPrice = formatPrice(ADDITIONAL_PRICE_AMOUNT);

  const validateEmail = (value: string) => {
    if (!value.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address.';
    return '';
  };

  const handleSubscribe = async () => {
    const emailErr = validateEmail(email);
    if (emailErr) {
      setEmailError(emailErr);
      return;
    }
    setEmailError('');
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Only the email is sent — the server reads the price ID from its env
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      const data = await res.json();

      // Account already exists — server blocked the checkout before any charge.
      // Redirect to login instead of showing a generic error.
      if (res.status === 409 && data.code === 'ACCOUNT_EXISTS') {
        window.location.href = '/login-screen';
        return;
      }

      if (!res.ok || !data.url) {
        setError(data.error ?? 'Failed to start checkout. Please try again.');
        return;
      }

      // Redirect to Stripe Checkout (or existing open session)
      window.location.href = data.url;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-surface px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppLogo size={36} />
            <span className="text-lg font-bold text-foreground">City Happenings</span>
          </div>
          <Link
            href="/login-screen"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Already a subscriber? Log in
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="max-w-2xl w-full text-center mb-12">
          <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">Pricing</p>
          <h1 className="text-4xl font-extrabold text-foreground mb-4 leading-tight">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-muted-foreground">
            One subscription covers your first city. Add more cities anytime at a reduced rate.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="max-w-2xl w-full grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
          {/* First city */}
          <div className="bg-surface border-2 border-primary rounded-2xl p-8 flex flex-col relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-bl-xl">
              Included
            </div>
            <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-2">First City</p>
            <div className="flex items-end gap-1 mb-1">
              <span className="text-5xl font-extrabold text-foreground">${basePrice.dollars}</span>
              <span className="text-2xl font-bold text-foreground mb-1">.{basePrice.cents}</span>
              <span className="text-muted-foreground text-sm mb-2">/month</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">Everything you need to manage one city&apos;s content.</p>
            <ul className="space-y-2.5 text-sm text-foreground mb-8">
              {[
                'AI-generated Facebook posts',
                'Post review & approval workflow',
                'Newsletter management',
                'City content dashboard',
                'Email support',
              ]?.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Additional cities */}
          <div className="bg-surface border border-border rounded-2xl p-8 flex flex-col">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Each Additional City</p>
            <div className="flex items-end gap-1 mb-1">
              <span className="text-5xl font-extrabold text-foreground">${additionalPrice.dollars}</span>
              <span className="text-2xl font-bold text-foreground mb-1">.{additionalPrice.cents}</span>
              <span className="text-muted-foreground text-sm mb-2">/month</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">Scale to as many cities as you manage.</p>
            <ul className="space-y-2.5 text-sm text-foreground mb-8">
              {[
                'All features included',
                'Same AI-powered automation',
                'Separate content dashboard',
                'Billed alongside your base plan',
              ]?.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0 text-xs font-bold">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Email input + CTA */}
        <div className="max-w-md w-full">
          <div className="mb-3">
            <label htmlFor="subscribe-email" className="block text-sm font-medium text-foreground mb-1.5">
              Your email address
            </label>
            <input
              id="subscribe-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
              }}
              className="w-full border border-border rounded-xl px-4 py-3 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
              autoComplete="email"
              disabled={loading}
            />
            {emailError && (
              <p className="mt-1.5 text-xs text-red-600">{emailError}</p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600 text-center">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full bg-primary text-white font-bold py-4 px-8 rounded-2xl text-lg hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed mb-3 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Redirecting to checkout…
              </>
            ) : (
              `Subscribe Now — $${BASE_PRICE_AMOUNT.toFixed(2)}/month`
            )}
          </button>
          <p className="text-xs text-muted-foreground text-center">
            Secure checkout powered by Stripe. Cancel anytime.
          </p>
        </div>

        {/* Back to login */}
        <p className="mt-10 text-sm text-muted-foreground">
          Already subscribed?{' '}
          <Link href="/login-screen" className="font-semibold text-primary hover:text-primary/80 transition-colors">
            Log in to your account
          </Link>
        </p>
      </main>
    </div>
  );
}
